import { motion } from "framer-motion";
import { Brain, Heart, Users, Sparkles, ArrowRight, Droplets, Award, Leaf } from "lucide-react";
import spaImg from "@/assets/spa-ambiance-opt.webp";
import serviceImg from "@/assets/service-detail-opt.webp";
import essentialOilsImg from "@/assets/essential-oils.jpg";
import defaultLogo from "@/assets/logo-mente-sa.png";
import { useLocalizedContent } from "@/hooks/use-localized-content";
import { useTranslation } from "react-i18next";

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.12, duration: 0.5 },
  }),
};

const Services = () => {
  const { c, content } = useLocalizedContent();
  const { t } = useTranslation();
  const badge = c("services_badge", "servicesSection.badge");
  const sectionTitle = c("services_title", "servicesSection.title");
  const sectionSubtitle = c("services_subtitle", "servicesSection.subtitle");
  const sectionImage = content.services_image ?? serviceImg;
  const bgImage = content.services_bg_image ?? spaImg;

  const aromaBadge = c("aroma_badge", "servicesSection.aromaBadge");
  const aromaTitle = c("aroma_title", "servicesSection.aromaTitle");
  const aromaSubtitle = c("aroma_subtitle", "servicesSection.aromaSubtitle");
  const aromaText = c("aroma_text", "servicesSection.aromaText");
  const aromaCta = c("aroma_cta_text", "servicesSection.aromaCta");

  const services = [
    {
      icon: Heart,
      title: c("service_massage_title", "services.massage"),
      description: c("service_massage_desc", "services.massageDesc"),
      tag: c("services_most_popular", "services.mostPopular"),
    },
    {
      icon: Users,
      title: c("service_child_title", "services.childMassage"),
      description: c("service_child_desc", "services.childMassageDesc"),
      tag: null,
    },
    {
      icon: Brain,
      title: c("service_anxiety_title", "services.anxiety"),
      description: c("service_anxiety_desc", "services.anxietyDesc"),
      tag: null,
    },
    {
      icon: Sparkles,
      title: c("service_aroma_title", "services.aromatherapy"),
      description: c("service_aroma_desc", "services.aromatherapyDesc"),
      tag: c("services_premium", "services.premium"),
    },
  ];

  const aromaHighlights = [
    {
      icon: Award,
      title: c("aroma_highlight1_title", "servicesSection.aromaHighlight1Title"),
      text: c("aroma_highlight1_text", "servicesSection.aromaHighlight1Text"),
    },
    {
      icon: Droplets,
      title: c("aroma_highlight2_title", "servicesSection.aromaHighlight2Title"),
      text: c("aroma_highlight2_text", "servicesSection.aromaHighlight2Text"),
    },
    {
      icon: Leaf,
      title: c("aroma_highlight3_title", "servicesSection.aromaHighlight3Title"),
      text: c("aroma_highlight3_text", "servicesSection.aromaHighlight3Text"),
    },
  ];

  const titleWords = sectionTitle.split(" ");
  const highlight = titleWords.slice(-1).join(" ");
  const before = titleWords.slice(0, -1).join(" ");

  return (
    <section id="servicos" className="py-24 relative overflow-hidden">
      {bgImage && (
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-10 pointer-events-none">
          <img src={bgImage} alt="" className="w-full h-full object-cover" aria-hidden="true" loading="lazy" />
        </div>
      )}

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="inline-block rounded-full bg-accent px-4 py-1.5 text-xs font-semibold text-accent-foreground mb-4">
              {badge}
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">
              {before}{" "}
              <span className="text-gradient-wine">{highlight}</span>
            </h2>
            <p className="text-muted-foreground max-w-xl text-lg leading-relaxed">{sectionSubtitle}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="hidden lg:block">
            {sectionImage && (
              <img src={sectionImage} alt="Serviços Mente Sã massagem relaxante aromaterapia óleos essenciais massagem crianças gestão ansiedade drenagem linfática reflexologia spa ao domicílio Lisboa Oeiras Cascais" className="rounded-3xl shadow-2xl shadow-primary/10 w-full max-w-md ml-auto object-cover aspect-square" width={448} height={448} loading="lazy" />
            )}
          </motion.div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="lift-card group relative rounded-2xl bg-card border border-border p-8 hover:shadow-xl hover:shadow-primary/5 hover:border-primary/30 transition-all duration-500"
            >
              {service.tag && (
                <span className="absolute -top-3 right-4 rounded-full bg-primary px-3 py-1 text-[10px] font-bold text-primary-foreground">
                  {service.tag}
                </span>
              )}
              <div className="w-14 h-14 rounded-xl bg-accent flex items-center justify-center mb-6 group-hover:bg-primary transition-colors duration-300">
                <service.icon size={28} className="text-accent-foreground group-hover:text-primary-foreground transition-colors duration-300" />
              </div>
              <h3 className="font-display text-xl font-semibold mb-3">{service.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">{service.description}</p>
              <a href="#agendar" className="inline-flex items-center gap-1 text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {c("services_book_now", "services.bookNow")} <ArrowRight size={14} />
              </a>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-20 rounded-3xl bg-card border border-primary/20 p-8 md:p-12 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-accent/30 blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
          <div className="relative z-10 grid lg:grid-cols-[1fr_minmax(280px,380px)] gap-10 lg:gap-12 items-center">
            <div>
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-8">
                <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center shrink-0">
                  <Droplets size={32} className="text-accent-foreground" />
                </div>
                <div>
                  <span className="inline-block rounded-full bg-primary/10 px-3 py-1 text-[10px] font-bold text-primary mb-2">
                    {aromaBadge}
                  </span>
                  <h3 className="font-display text-2xl md:text-3xl font-bold text-foreground">{aromaTitle}</h3>
                  <p className="text-muted-foreground text-sm mt-1">{aromaSubtitle}</p>
                </div>
              </div>

              <p className="text-muted-foreground leading-relaxed max-w-3xl mb-8">{aromaText}</p>

              <div className="grid sm:grid-cols-3 gap-6">
                {aromaHighlights.map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + i * 0.1 }}
                    className="flex gap-4"
                  >
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <item.icon size={20} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="font-display font-semibold text-foreground mb-1">{item.title}</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">{item.text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8">
                <a href="#agendar" className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-primary-foreground hover:opacity-90 transition-opacity">
                  {aromaCta} <ArrowRight size={16} />
                </a>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative order-first lg:order-last"
            >
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-primary/15 aspect-square relative">
                <img
                  src={essentialOilsImg}
                  alt="Óleos essenciais puros de aromaterapia com lavanda e eucalipto, Mente Sã"
                  className="w-full h-full object-cover"
                  width={1024}
                  height={1024}
                  loading="lazy"
                />
                {/* Subtle brand watermark */}
                <div className="absolute top-4 right-4 flex items-center gap-2 rounded-full bg-background/70 backdrop-blur-md border border-white/40 pl-1.5 pr-3 py-1 shadow-lg">
                  <img
                    src={content.logo_url || defaultLogo}
                    alt="Mente Sã"
                    className="h-7 w-7 rounded-full object-cover"
                    width={28}
                    height={28}
                    loading="lazy"
                  />
                  <span className="font-display text-[11px] font-bold text-foreground tracking-wide">Mente Sã</span>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 hidden md:flex items-center gap-2 rounded-full bg-card border border-border px-4 py-2 shadow-lg">
                <Leaf size={16} className="text-primary" />
                <span className="text-xs font-semibold text-foreground">{c("aroma_natural_badge", "_none_", "100% Naturais")}</span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
